/**
 * Agent personas — operator ruling 2026-08-18: every agent-driven pop-up and
 * toast shows the SPEAKING agent's portrait + name, so the platform feels
 * like talking to people, not receiving system output.
 *
 * Portraits are web-optimized copies of the operator-verified CANONICAL
 * ledger files (assets/sigmavue/agents/<agent>/…) — never the legacy
 * docs/Agent-pics set, which contains wrong-brand traps. Jack Mercer has no
 * verified portrait and must never get one from the trap folders.
 *
 * Speaking-agent mapping (operator): PM rebalance = Sigma · risk/size
 * recommendations = Dollar Bill · desk updates/support = Donna.
 */

export type AgentPersonaKey = "sigma" | "dollar_bill" | "donna";

export interface AgentPersona {
  key: AgentPersonaKey;
  name: string;
  portrait: string;
  /** Accent for the name/ring — matches the desk palette. */
  accent: string;
}

export const AGENT_PERSONAS: Record<AgentPersonaKey, AgentPersona> = {
  sigma: {
    key: "sigma",
    name: "Sigma",
    portrait: "/images/agents/sigma-portrait.jpg",
    accent: "hsl(160 65% 55%)",
  },
  dollar_bill: {
    key: "dollar_bill",
    name: "Dollar Bill",
    portrait: "/images/agents/dollar-bill-portrait.png",
    accent: "#22c55e",
  },
  donna: {
    key: "donna",
    name: "Donna",
    portrait: "/images/agents/donna-portrait.png",
    accent: "hsl(200 70% 62%)",
  },
};

/** Resolve a persona from an event's `persona` field; unknown/absent falls
 * back per surface (pop-ups default Sigma, desk toasts default Donna). */
export function agentPersona(
  key: unknown,
  fallback: AgentPersonaKey,
): AgentPersona {
  if (typeof key === "string" && key in AGENT_PERSONAS) {
    return AGENT_PERSONAS[key as AgentPersonaKey];
  }
  return AGENT_PERSONAS[fallback];
}
